import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API_BASE_URL as API } from '../config/api';
import { NextActionBar } from '../components/ContentLoop';
import {
  Zap, Music, Wand2, Loader2, ArrowLeft, Settings2,
  Lock, Sparkles, Play, Copy, ChevronDown, ChevronUp
} from 'lucide-react';

const GENRES = ['Hip-Hop', 'R&B', 'Pop', 'Trap', 'Drill', 'Boom Bap', 'Lo-Fi', 'Afrobeat', 'Reggaeton', 'Soul', 'Jazz', 'Electronic'];
const MOODS = ['Dark', 'Energetic', 'Chill', 'Aggressive', 'Emotional', 'Uplifting', 'Mysterious', 'Party', 'Melancholic', 'Triumphant'];
const TEMPOS = ['Slow (60-80 BPM)', 'Medium (80-110 BPM)', 'Fast (110-140 BPM)', 'Very Fast (140+ BPM)'];

export default function MusicStudioPage() {
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [mode, setMode] = useState('quick_cook');
  const [modes, setModes] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Form
  const [prompt, setPrompt] = useState('');
  const [genre, setGenre] = useState('');
  const [mood, setMood] = useState('');
  const [tempo, setTempo] = useState('');
  const [lyrics, setLyrics] = useState('');
  const [styleTags, setStyleTags] = useState('');

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    fetch(`${API}/api/music-gen/modes`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => setModes(d.modes || []))
      .catch(() => {});
  }, [isAuthenticated, navigate, getAuthHeaders]);

  const handleGenerate = async () => {
    if (!prompt.trim()) { toast.error('Describe what you want to create'); return; }
    setGenerating(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/music-gen/generate`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode,
          prompt: prompt.trim(),
          genre: genre || null,
          mood: mood || null,
          tempo: tempo || null,
          lyrics: lyrics || null,
          style_tags: styleTags ? styleTags.split(',').map(s => s.trim()) : null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        toast.success('Generation complete!');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Generation failed');
      }
    } catch (err) {
      toast.error('Failed to generate');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const studioMode = modes.find(m => m.id === 'studio');
  const studioAvailable = studioMode?.available;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="music-studio-page">
      <div className="sticky top-0 z-20 backdrop-blur-xl bg-[#0A0A0A]/70 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white" data-testid="studio-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold">Music Studio</h1>
            <p className="text-xs text-zinc-500">AI-powered music creation</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Mode Selector */}
        <div className="grid grid-cols-2 gap-3">
          <Card
            onClick={() => setMode('quick_cook')}
            className={`bg-[#141414] rounded-xl p-4 cursor-pointer transition-all border-2 ${
              mode === 'quick_cook' ? 'border-[#007AFF]/50 bg-[#007AFF]/5' : 'border-white/5 hover:border-white/15'
            }`}
            data-testid="mode-quick-cook"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#007AFF]/15 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#007AFF]" />
              </div>
              <div>
                <p className="font-semibold text-sm">Quick Cook</p>
                <p className="text-[10px] text-zinc-500">Beat concepts, lyrics, structure</p>
              </div>
            </div>
          </Card>

          <Card
            onClick={() => studioAvailable && setMode('studio')}
            className={`bg-[#141414] rounded-xl p-4 cursor-pointer transition-all border-2 ${
              mode === 'studio' ? 'border-[#A78BFA]/50 bg-[#A78BFA]/5' : 'border-white/5'
            } ${!studioAvailable ? 'opacity-60 cursor-not-allowed' : 'hover:border-white/15'}`}
            data-testid="mode-studio"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#A78BFA]/15 flex items-center justify-center">
                {studioAvailable ? <Sparkles className="w-5 h-5 text-[#A78BFA]" /> : <Lock className="w-5 h-5 text-zinc-500" />}
              </div>
              <div>
                <p className="font-semibold text-sm">Studio Mode</p>
                <p className="text-[10px] text-zinc-500">{studioAvailable ? 'Full song generation' : 'Premium API required'}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Generation Form */}
        <Card className="bg-[#141414] border-white/10 rounded-xl p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-400 block mb-2">Describe your vision</label>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., Hard-hitting trap beat with 808s and dark synths, perfect for a street anthem..."
              className="bg-[#1C1C1E] border-white/10 text-white placeholder:text-zinc-600 min-h-[100px] resize-none"
              data-testid="prompt-input"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Genre</label>
              <Select value={genre} onValueChange={setGenre}>
                <SelectTrigger className="bg-[#1C1C1E] border-white/10 text-sm h-9" data-testid="genre-select">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1E] border-white/10">
                  {GENRES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Mood</label>
              <Select value={mood} onValueChange={setMood}>
                <SelectTrigger className="bg-[#1C1C1E] border-white/10 text-sm h-9" data-testid="mood-select">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1E] border-white/10">
                  {MOODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Tempo</label>
              <Select value={tempo} onValueChange={setTempo}>
                <SelectTrigger className="bg-[#1C1C1E] border-white/10 text-sm h-9" data-testid="tempo-select">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1E] border-white/10">
                  {TEMPOS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Advanced Options Toggle */}
          <Button variant="ghost" onClick={() => setShowAdvanced(!showAdvanced)} className="text-xs text-zinc-400 p-0 h-auto" data-testid="advanced-toggle">
            <Settings2 className="w-3.5 h-3.5 mr-1.5" />
            Advanced Options
            {showAdvanced ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
          </Button>

          {showAdvanced && (
            <div className="space-y-3 pt-2 border-t border-white/5">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Style Tags (comma separated)</label>
                <Input
                  value={styleTags}
                  onChange={(e) => setStyleTags(e.target.value)}
                  placeholder="e.g., 808 bass, dark synths, hi-hats"
                  className="bg-[#1C1C1E] border-white/10 text-sm h-9"
                  data-testid="style-tags-input"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Lyrics (optional)</label>
                <Textarea
                  value={lyrics}
                  onChange={(e) => setLyrics(e.target.value)}
                  placeholder="Paste existing lyrics or a hook to build around..."
                  className="bg-[#1C1C1E] border-white/10 text-sm min-h-[80px] resize-none"
                  data-testid="lyrics-input"
                />
              </div>
            </div>
          )}

          <Button
            onClick={handleGenerate}
            disabled={generating || !prompt.trim()}
            className="w-full h-11 bg-[#007AFF] hover:bg-[#0066DD] font-semibold rounded-xl"
            data-testid="generate-btn"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" />Generating...</>
            ) : (
              <><Wand2 className="w-4 h-4 mr-2" />{mode === 'quick_cook' ? 'Quick Cook' : 'Generate Song'}</>
            )}
          </Button>
        </Card>

        {/* Result */}
        {result?.result && (
          <Card className="bg-[#141414] border-[#34D399]/30 rounded-xl overflow-hidden" data-testid="generation-result">
            <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Music className="w-4 h-4 text-[#34D399]" />
                <h3 className="text-sm font-semibold text-zinc-300">
                  {typeof result.result === 'object' ? result.result.title || 'Generated Concept' : 'Generated Concept'}
                </h3>
              </div>
              <Badge className="bg-[#34D399]/15 text-[#34D399] border-0 text-[10px]">{result.provider}</Badge>
            </div>
            <div className="p-5 space-y-4">
              {typeof result.result === 'object' ? (
                <>
                  {result.result.lyrics && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Lyrics</p>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-zinc-500" onClick={() => copyToClipboard(result.result.lyrics)}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                      <pre className="text-sm text-zinc-300 whitespace-pre-wrap bg-white/5 rounded-lg p-3 max-h-[200px] overflow-y-auto font-sans leading-relaxed">
                        {result.result.lyrics}
                      </pre>
                    </div>
                  )}
                  {result.result.structure && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">Song Structure</p>
                      <p className="text-sm text-zinc-300">{typeof result.result.structure === 'string' ? result.result.structure : JSON.stringify(result.result.structure)}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-3">
                    {result.result.bpm && (
                      <div className="bg-white/5 rounded-lg p-2 text-center">
                        <p className="text-[10px] text-zinc-500">BPM</p>
                        <p className="text-sm font-bold">{result.result.bpm}</p>
                      </div>
                    )}
                    {result.result.key && (
                      <div className="bg-white/5 rounded-lg p-2 text-center">
                        <p className="text-[10px] text-zinc-500">Key</p>
                        <p className="text-sm font-bold">{result.result.key}</p>
                      </div>
                    )}
                    {result.result.instruments && (
                      <div className="bg-white/5 rounded-lg p-2 text-center">
                        <p className="text-[10px] text-zinc-500">Instruments</p>
                        <p className="text-[10px]">{Array.isArray(result.result.instruments) ? result.result.instruments.join(', ') : result.result.instruments}</p>
                      </div>
                    )}
                  </div>
                  {result.result.production_notes && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">Production Notes</p>
                      <p className="text-sm text-zinc-400">{result.result.production_notes}</p>
                    </div>
                  )}
                </>
              ) : (
                <pre className="text-sm text-zinc-300 whitespace-pre-wrap">{JSON.stringify(result.result, null, 2)}</pre>
              )}

              {result.audio_url && (
                <div className="pt-3 border-t border-white/5">
                  <audio controls className="w-full" src={result.audio_url}>
                    Your browser does not support audio playback.
                  </audio>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Next Actions */}
        {result && <NextActionBar context="after_beat" />}
      </div>
    </div>
  );
}
